import { useState, useMemo, useRef, useEffect } from 'react'
import { Send, Loader, MessageSquare, Sparkles, Check, Pencil, X, AlertCircle, TrendingUp, ThumbsUp, ThumbsDown, Minus } from 'lucide-react'
import ReactECharts from 'echarts-for-react'
import { useApi, asArray } from '../../hooks/useApi'
import { apiPost, apiPatch, apiPut } from '../../lib/auth'
import { StarRating } from '../../components/StarRating'

interface Review {
  id: number
  locationId: number
  authorName: string
  profilePhoto: string | null
  rating: number
  comment: string | null
  replyText: string | null
  reviewTime: string
  googleId: string
  replied?: boolean
  replyTime?: string | null
  replyStatus?: 'pending_reply' | 'draft_ready' | 'replied' | 'dismissed'
  suggestedReply?: string | null
  sentiment?: 'positive' | 'negative' | 'neutral' | null
  themes?: string[] | null
}

interface ReputationProps {
  locationId: number | null
}

type RatingFilter = 'all' | 1 | 2 | 3 | 4 | 5
type StatusFilter = 'all' | 'pending_reply' | 'draft_ready' | 'replied' | 'dismissed'

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return "&agrave; l'instant"
  if (minutes < 60) return `il y a ${minutes} min`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `il y a ${hours}h`
  const days = Math.floor(hours / 24)
  if (days < 7) return `il y a ${days} jour${days > 1 ? 's' : ''}`
  if (days < 30) return `il y a ${Math.floor(days / 7)} sem.`
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function SentimentBadge({ sentiment }: { sentiment: string }) {
  const map: Record<string, { label: string; cls: string; icon: typeof ThumbsUp }> = {
    positive: { label: 'Positif', cls: 'bg-emerald-500/10 text-emerald-600', icon: ThumbsUp },
    negative: { label: 'N&eacute;gatif', cls: 'bg-red-500/10 text-red-500', icon: ThumbsDown },
    neutral: { label: 'Neutre', cls: 'bg-gray-200/60 text-gray-500', icon: Minus },
  }
  const info = map[sentiment]
  if (!info) return null
  const Icon = info.icon
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${info.cls}`}>
      <Icon className="w-3 h-3" strokeWidth={1.5} />
      {info.label}
    </span>
  )
}

function ThemeChips({ themes }: { themes: string[] }) {
  return (
    <div className="flex flex-wrap gap-1 mt-1.5">
      {themes.map(t => (
        <span key={t} className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#EEF2FF] text-[#6366F1]">
          {t}
        </span>
      ))}
    </div>
  )
}

function RatingDistributionBar({ reviews }: { reviews: Review[] }) {
  const total = reviews.length
  if (total === 0) return null

  const counts = [5, 4, 3, 2, 1].map(star => ({
    star,
    count: reviews.filter(r => r.rating === star).length,
    pct: Math.round((reviews.filter(r => r.rating === star).length / total) * 100),
  }))

  const avg = (reviews.reduce((s, r) => s + r.rating, 0) / total).toFixed(1)

  return (
    <div className="bg-white border border-[#E2E4ED] rounded-xl p-5 space-y-4">
      <div className="flex items-start gap-6">
        {/* Average score */}
        <div className="text-center shrink-0">
          <p className="text-4xl font-mono font-bold text-[#1B2559]">{avg}</p>
          <StarRating rating={Math.round(Number(avg))} size={14} />
          <p className="text-xs text-[#555A73] mt-1">{total} avis</p>
        </div>

        {/* Distribution bars */}
        <div className="flex-1 space-y-1.5">
          {counts.map(({ star, count, pct }) => (
            <div key={star} className="flex items-center gap-2">
              <span className="text-xs text-[#8B8FA7] w-2 shrink-0">{star}</span>
              <div className="flex-1 h-2 bg-[#F4F5FA] rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-400 rounded-full transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <span className="text-xs text-[#555A73] w-6 shrink-0 text-right">{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function RatingEvolutionChart({ reviews }: { reviews: Review[] }) {
  const chartData = useMemo(() => {
    if (reviews.length < 2) return null
    const sorted = [...reviews].sort((a, b) => new Date(a.reviewTime).getTime() - new Date(b.reviewTime).getTime())
    const monthly: Record<string, { sum: number; count: number }> = {}
    sorted.forEach(r => {
      const key = r.reviewTime.slice(0, 7) // YYYY-MM
      if (!monthly[key]) monthly[key] = { sum: 0, count: 0 }
      monthly[key].sum += r.rating
      monthly[key].count += 1
    })
    const entries = Object.entries(monthly).sort(([a], [b]) => a.localeCompare(b))
    if (entries.length < 2) return null
    return {
      dates: entries.map(([k]) => {
        const [y, m] = k.split('-')
        return `${m}/${y.slice(2)}`
      }),
      averages: entries.map(([, v]) => Number((v.sum / v.count).toFixed(2))),
    }
  }, [reviews])

  if (!chartData) return null

  const option = {
    grid: { top: 10, right: 10, bottom: 24, left: 36 },
    xAxis: {
      type: 'category' as const,
      data: chartData.dates,
      axisLabel: { fontSize: 10, color: '#8B8FA7' },
      axisLine: { lineStyle: { color: '#E2E4ED' } },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value' as const,
      min: 1,
      max: 5,
      axisLabel: { fontSize: 10, color: '#8B8FA7' },
      splitLine: { lineStyle: { color: '#F4F5FA' } },
    },
    series: [{
      data: chartData.averages,
      type: 'line' as const,
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      lineStyle: { color: '#6366F1', width: 2 },
      itemStyle: { color: '#6366F1' },
      areaStyle: {
        color: {
          type: 'linear' as const,
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(99,102,241,0.15)' },
            { offset: 1, color: 'rgba(99,102,241,0)' },
          ],
        },
      },
    }],
    tooltip: {
      trigger: 'axis' as const,
      formatter: (params: Array<{ name: string; value: number }>) => {
        const p = params[0]
        return `${p.name}<br/>Note moyenne : <b>${p.value}</b>`
      },
    },
  }

  return (
    <div className="bg-white border border-[#E2E4ED] rounded-xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-[#6366F1]" strokeWidth={1.25} />
        <p className="text-sm font-medium text-[#1B2559]">&Eacute;volution de la note moyenne</p>
      </div>
      <ReactECharts option={option} style={{ height: 180 }} />
    </div>
  )
}

function DraftBox({
  review,
  onAccept,
  onDismiss,
  onEditSave,
  accepting,
}: {
  review: Review
  onAccept: (id: number) => void
  onDismiss: (id: number) => void
  onEditSave: (id: number, text: string) => void
  accepting: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [editText, setEditText] = useState(review.suggestedReply ?? '')

  return (
    <div className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-lg p-3 space-y-2">
      <p className="text-xs font-medium text-[#6366F1] flex items-center gap-1.5">
        <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />
        R&eacute;ponse sugg&eacute;r&eacute;e par l&rsquo;IA
      </p>
      {editing ? (
        <>
          <textarea
            value={editText}
            onChange={e => setEditText(e.target.value)}
            rows={3}
            className="w-full bg-white border border-[#C7D2FE] rounded-lg px-3 py-2 text-sm text-[#1B2559] focus:outline-none focus:border-[#6366F1] resize-none"
          />
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setEditing(false)}
              className="px-3 py-1.5 text-xs text-[#8B8FA7] hover:text-[#1B2559] transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={() => {
                onEditSave(review.id, editText)
                setEditing(false)
              }}
              disabled={!editText.trim()}
              className="px-3 py-1.5 text-xs font-medium text-white bg-[#6366F1] rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity"
            >
              Enregistrer
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-sm text-[#1B2559] leading-relaxed">{review.suggestedReply}</p>
          <div className="flex gap-2">
            <button
              onClick={() => onAccept(review.id)}
              disabled={accepting}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-emerald-500 rounded-lg hover:bg-emerald-600 disabled:opacity-50 transition-colors"
            >
              {accepting ? <Loader className="w-3 h-3 animate-spin" strokeWidth={1.5} /> : <Check className="w-3 h-3" strokeWidth={1.5} />}
              Accepter
            </button>
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#6366F1] bg-white border border-[#C7D2FE] rounded-lg hover:bg-[#EEF2FF] transition-colors"
            >
              <Pencil className="w-3 h-3" strokeWidth={1.5} />
              Modifier
            </button>
            <button
              onClick={() => onDismiss(review.id)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-[#8B8FA7] bg-[#F4F5FA] rounded-lg hover:bg-[#E2E4ED] transition-colors"
            >
              <X className="w-3 h-3" strokeWidth={1.5} />
              Ignorer
            </button>
          </div>
        </>
      )}
    </div>
  )
}

function ReplyForm({
  locationId,
  reviewId,
  initialText,
  onReplied,
}: {
  locationId: number
  reviewId: number
  initialText?: string
  onReplied: () => void
}) {
  const [text, setText] = useState(initialText ?? '')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSend = async () => {
    if (!text.trim()) return
    setSending(true)
    setError(null)
    const { error: err } = await apiPost(`/api/local/locations/${locationId}/reviews/${reviewId}/reply`, {
      comment: text.trim(),
    })
    setSending(false)
    if (err) {
      setError(err)
      return
    }
    setText('')
    onReplied()
  }

  return (
    <div className="mt-3 space-y-2">
      <textarea
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder="R&eacute;digez votre r&eacute;ponse&hellip;"
        rows={3}
        className="w-full bg-[#F4F5FA] border border-[#E2E4ED] rounded-lg px-3 py-2 text-sm text-[#1B2559] placeholder-[#555A73] focus:outline-none focus:border-orion-primary/50 resize-none"
      />
      {error && <p className="text-score-critical text-xs">{error}</p>}
      <div className="flex justify-end">
        <button
          onClick={handleSend}
          disabled={sending || !text.trim()}
          className="flex items-center gap-1.5 px-3.5 py-2 bg-gradient-to-r from-orion-primary to-[#7C3AED] text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {sending
            ? <Loader className="w-3.5 h-3.5 animate-spin" strokeWidth={1.25} />
            : <Send className="w-3.5 h-3.5" strokeWidth={1.25} />
          }
          R&eacute;pondre
        </button>
      </div>
    </div>
  )
}

function GenerateDraftsButton({ locationId, onGenerated }: { locationId: number; onGenerated: () => void }) {
  const [open, setOpen] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [result, setResult] = useState<string | null>(null)
  const popRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const generate = async (tone: string) => {
    setGenerating(true)
    setResult(null)
    const { data, error } = await apiPost<{ generated: number }>(`/api/local/locations/${locationId}/reviews/generate-drafts`, { tone })
    setGenerating(false)
    if (error) {
      setResult(`Erreur : ${error}`)
    } else {
      setResult(`${(data as { generated: number })?.generated ?? 0} r&eacute;ponse(s) g&eacute;n&eacute;r&eacute;e(s)`)
      setTimeout(() => {
        setOpen(false)
        setResult(null)
        onGenerated()
      }, 1500)
    }
  }

  const tones = [
    { value: 'professionnel', label: 'Professionnel' },
    { value: 'chaleureux', label: 'Chaleureux' },
    { value: 'décontracté', label: 'D&eacute;contract&eacute;' },
  ]

  return (
    <div className="relative" ref={popRef}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2 px-3.5 py-2 bg-gradient-to-r from-orion-primary to-[#7C3AED] text-white text-sm font-medium rounded-xl hover:opacity-90 transition-opacity"
      >
        <Sparkles className="w-4 h-4" strokeWidth={1.25} />
        G&eacute;n&eacute;rer les r&eacute;ponses IA
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-2 z-20 bg-white border border-[#E2E4ED] rounded-xl shadow-lg p-4 w-64 space-y-3">
          <p className="text-sm font-medium text-[#1B2559]">Choisir le ton</p>
          {generating ? (
            <div className="flex items-center justify-center py-4">
              <Loader className="w-5 h-5 animate-spin text-[#6366F1]" strokeWidth={1.5} />
            </div>
          ) : result ? (
            <p className="text-sm text-emerald-600 text-center py-2">{result}</p>
          ) : (
            <div className="space-y-2">
              {tones.map(t => (
                <button
                  key={t.value}
                  onClick={() => generate(t.value)}
                  className="w-full text-left px-3 py-2 text-sm text-[#1B2559] rounded-lg hover:bg-[#F4F5FA] transition-colors"
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export function Reputation({ locationId }: ReputationProps) {
  const [ratingFilter, setRatingFilter] = useState<RatingFilter>('all')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [replyingTo, setReplyingTo] = useState<number | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [acceptingId, setAcceptingId] = useState<number | null>(null)
  const [bulkAccepting, setBulkAccepting] = useState(false)

  const { data, loading, error, refetch } = useApi<{
    reviews: Review[]
    averageRating: number
    totalCount: number
  }>(locationId ? `/api/local/locations/${locationId}/reviews` : '')

  if (!locationId) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-[#8B8FA7] text-sm">S&eacute;lectionnez un &eacute;tablissement pour voir ses avis.</p>
      </div>
    )
  }

  const reviewList = asArray(data?.reviews)

  // Count pending
  const pendingCount = reviewList.filter(
    r => r.replyStatus === 'pending_reply' || r.replyStatus === 'draft_ready'
  ).length
  const draftReadyIds = reviewList.filter(r => r.replyStatus === 'draft_ready' && r.suggestedReply).map(r => r.id)

  // Apply filters
  let filtered = ratingFilter === 'all'
    ? reviewList
    : reviewList.filter(r => r.rating === ratingFilter)

  if (statusFilter !== 'all') {
    filtered = filtered.filter(r => r.replyStatus === statusFilter)
  }

  const RATING_FILTERS: { value: RatingFilter; label: string }[] = [
    { value: 'all', label: 'Tous' },
    { value: 5, label: '5 \u2605' },
    { value: 4, label: '4 \u2605' },
    { value: 3, label: '3 \u2605' },
    { value: 2, label: '2 \u2605' },
    { value: 1, label: '1 \u2605' },
  ]

  const STATUS_FILTERS: { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'Tous statuts' },
    { value: 'pending_reply', label: 'En attente' },
    { value: 'draft_ready', label: 'Draft pr\u00EAt' },
    { value: 'replied', label: 'R\u00E9pondu' },
    { value: 'dismissed', label: 'Ignor\u00E9' },
  ]

  // Handlers
  const handleAcceptDraft = async (reviewId: number) => {
    setAcceptingId(reviewId)
    await apiPost(`/api/local/reviews/${reviewId}/accept-draft`, {})
    setAcceptingId(null)
    refetch()
  }

  const handleDismiss = async (reviewId: number) => {
    await apiPatch(`/api/local/reviews/${reviewId}/dismiss`, { status: 'dismissed' })
    refetch()
  }

  const handleEditDraft = async (reviewId: number, text: string) => {
    await apiPut(`/api/local/reviews/${reviewId}/edit-draft`, { suggestedReply: text })
    refetch()
  }

  const handleBulkAccept = async () => {
    if (selectedIds.size === 0) return
    setBulkAccepting(true)
    await apiPost('/api/local/reviews/bulk-accept', { reviewIds: Array.from(selectedIds) })
    setBulkAccepting(false)
    setSelectedIds(new Set())
    refetch()
  }

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === draftReadyIds.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(draftReadyIds))
    }
  }

  return (
    <div className="space-y-5">
      {/* Pending alert */}
      {pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-amber-400/20 flex items-center justify-center shrink-0">
            <AlertCircle className="w-4 h-4 text-amber-600" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-amber-800">
              {pendingCount} avis en attente de r&eacute;ponse
            </p>
            <p className="text-xs text-amber-600 mt-0.5">
              {draftReadyIds.length > 0
                ? `${draftReadyIds.length} draft(s) IA pr\u00EAt(s) \u00E0 envoyer`
                : 'Utilisez la g\u00E9n\u00E9ration IA pour cr\u00E9er des brouillons'}
            </p>
          </div>
          <span className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full bg-amber-400 text-white text-sm font-bold">
            {pendingCount}
          </span>
        </div>
      )}

      {/* Top actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          {draftReadyIds.length > 0 && (
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[#6366F1] bg-[#EEF2FF] rounded-lg hover:bg-[#E0E7FF] transition-colors"
            >
              <Check className="w-3 h-3" strokeWidth={1.5} />
              {selectedIds.size === draftReadyIds.length ? 'D\u00E9s\u00E9lectionner tout' : 'S\u00E9lectionner les drafts'}
            </button>
          )}
        </div>
        <GenerateDraftsButton locationId={locationId} onGenerated={refetch} />
      </div>

      {/* Distribution */}
      {reviewList.length > 0 && (
        <RatingDistributionBar reviews={reviewList} />
      )}

      {/* Evolution chart */}
      {reviewList.length > 0 && (
        <RatingEvolutionChart reviews={reviewList} />
      )}

      {/* Filter bars */}
      <div className="space-y-2">
        {/* Rating filters */}
        <div className="flex gap-2 flex-wrap">
          {RATING_FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setRatingFilter(f.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                ratingFilter === f.value
                  ? 'bg-orion-primary text-white'
                  : 'bg-[#F4F5FA] text-[#8B8FA7] hover:text-[#1B2559]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        {/* Status filters */}
        <div className="flex gap-2 flex-wrap">
          {STATUS_FILTERS.map(f => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                statusFilter === f.value
                  ? 'bg-[#6366F1] text-white'
                  : 'bg-[#F4F5FA] text-[#8B8FA7] hover:text-[#1B2559]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-orion-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="bg-score-critical/10 border border-score-critical/20 rounded-xl p-4 text-score-critical text-sm">
          {error}
        </div>
      )}

      {/* Empty */}
      {!loading && !error && reviewList.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-14 h-14 rounded-2xl bg-white border border-[#E2E4ED] flex items-center justify-center mb-4">
            <MessageSquare className="w-6 h-6 text-[#555A73]" strokeWidth={1.25} />
          </div>
          <p className="text-[#1B2559] font-semibold mb-1">Aucun avis</p>
          <p className="text-[#8B8FA7] text-sm">Les avis Google de cet &eacute;tablissement appara&icirc;tront ici.</p>
        </div>
      )}

      {/* Reviews list */}
      {!loading && filtered.length > 0 && (
        <div className="space-y-3">
          {filtered.map(review => {
            const hasDraft = review.replyStatus === 'draft_ready' && review.suggestedReply
            return (
              <div
                key={review.id}
                className="bg-white border border-[#E2E4ED] rounded-xl p-5 space-y-3"
              >
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {/* Checkbox for drafts */}
                    {hasDraft && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(review.id)}
                        onChange={() => toggleSelect(review.id)}
                        className="w-4 h-4 rounded border-[#E2E4ED] text-[#6366F1] focus:ring-[#6366F1] shrink-0"
                      />
                    )}
                    {/* Avatar */}
                    {review.profilePhoto ? (
                      <img
                        src={review.profilePhoto}
                        alt={review.authorName}
                        className="w-9 h-9 rounded-full object-cover shrink-0"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#6366F1] to-[#7C3AED] flex items-center justify-center shrink-0">
                        <span className="text-sm font-medium text-white">
                          {review.authorName?.charAt(0)?.toUpperCase() ?? '?'}
                        </span>
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-[#1B2559] truncate">{review.authorName}</p>
                      <p className="text-xs text-[#555A73]">{timeAgo(review.reviewTime)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {review.sentiment && <SentimentBadge sentiment={review.sentiment} />}
                    <StarRating rating={review.rating} size={14} />
                  </div>
                </div>

                {/* Comment */}
                {review.comment && (
                  <p className="text-sm text-[#8B8FA7] leading-relaxed">{review.comment}</p>
                )}

                {/* Theme chips */}
                {review.themes && review.themes.length > 0 && (
                  <ThemeChips themes={review.themes} />
                )}

                {/* Existing reply */}
                {review.replyText && (
                  <div className="bg-[#F4F5FA] border-l-2 border-orion-primary/40 rounded-r-lg pl-3 py-2 pr-3">
                    <p className="text-xs text-[#555A73] mb-1 font-medium">Votre r&eacute;ponse</p>
                    <p className="text-sm text-[#8B8FA7]">{review.replyText}</p>
                  </div>
                )}

                {/* AI Draft */}
                {!review.replyText && hasDraft && (
                  <DraftBox
                    review={review}
                    onAccept={handleAcceptDraft}
                    onDismiss={handleDismiss}
                    onEditSave={handleEditDraft}
                    accepting={acceptingId === review.id}
                  />
                )}

                {/* Reply form or button */}
                {!review.replyText && !hasDraft && (
                  replyingTo === review.id ? (
                    <ReplyForm
                      locationId={review.locationId}
                      reviewId={review.id}
                      onReplied={() => {
                        setReplyingTo(null)
                        refetch()
                      }}
                    />
                  ) : (
                    <button
                      onClick={() => setReplyingTo(review.id)}
                      className="flex items-center gap-1.5 text-xs text-[#8B8FA7] hover:text-[#1B2559] transition-colors"
                    >
                      <MessageSquare className="w-3.5 h-3.5" strokeWidth={1.25} />
                      R&eacute;pondre
                    </button>
                  )
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Filtered empty */}
      {!loading && reviewList.length > 0 && filtered.length === 0 && (
        <div className="text-center py-10">
          <p className="text-[#8B8FA7] text-sm">Aucun avis avec ces filtres.</p>
        </div>
      )}

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#1B2559] text-white rounded-2xl shadow-2xl px-6 py-3 flex items-center gap-4">
          <p className="text-sm font-medium">
            {selectedIds.size} s&eacute;lectionn&eacute;{selectedIds.size > 1 ? 's' : ''}
          </p>
          <button
            onClick={handleBulkAccept}
            disabled={bulkAccepting}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white text-sm font-medium rounded-xl hover:bg-emerald-600 disabled:opacity-50 transition-colors"
          >
            {bulkAccepting
              ? <Loader className="w-3.5 h-3.5 animate-spin" strokeWidth={1.5} />
              : <Check className="w-3.5 h-3.5" strokeWidth={1.5} />
            }
            Accepter tout
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-white/60 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" strokeWidth={1.5} />
          </button>
        </div>
      )}
    </div>
  )
}
